import {
  useQuery,
  useSuspenseQuery,
  UseQueryOptions,
  UseSuspenseQueryOptions,
  QueryKey,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { apiFetch, FetcherOptions } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';

export function createKey(
  tenantAlias: string,
  endpoint: ApiEndpoint,
  opts?: Pick<FetcherOptions, 'query' | 'body' | 'pathParams'>,
): QueryKey {
  return [
    'KSAI-DEEP-ENTERPRISE',
    tenantAlias,
    endpoint,
    opts?.query ? JSON.stringify(opts.query) : null,
    opts?.body ? JSON.stringify(opts.body) : null,
    opts?.pathParams ? JSON.stringify(opts.pathParams) : null,
  ] as const;
}

export function useApiQuery<TResponse = unknown>(
  tenantAlias: string,
  endpoint: ApiEndpoint,
  opts?: FetcherOptions,
  options?: Omit<UseQueryOptions<TResponse, Error, TResponse, QueryKey>, 'queryKey' | 'queryFn'>,
) {
  const key = createKey(tenantAlias, endpoint, {
    query: opts?.query,
    body: opts?.body,
    pathParams: opts?.pathParams,
  });

  return useQuery<TResponse, Error, TResponse, QueryKey>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: key,
    queryFn: () => apiFetch<TResponse>(tenantAlias, endpoint, opts),
    ...options,
  });
}

export function useSuspenseApiQuery<TResponse = unknown>(
  tenantAlias: string,
  endpoint: ApiEndpoint,
  opts?: FetcherOptions,
  options?: Omit<UseSuspenseQueryOptions<TResponse, Error, TResponse, QueryKey>, 'queryKey' | 'queryFn'>,
): UseSuspenseQueryResult<TResponse, Error> {
  const key = createKey(tenantAlias, endpoint, {
    query: opts?.query,
    body: opts?.body,
    pathParams: opts?.pathParams,
  });

  return useSuspenseQuery<TResponse, Error, TResponse, QueryKey>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: key,
    queryFn: () => apiFetch<TResponse>(tenantAlias, endpoint, opts),
    ...options,
  });
}
